import { DatePipe, NgClass, NgIf } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  ChangeOwnPasswordDto,
  LocalPasswordChangeResponseDto,
  LocalUserDto,
  LocalUserLoginHistoryDto,
  LocalUserSessionDto,
  LocalUsersApi,
  RevokeAllSessionsResponseDto,
  RevokeSessionResponseDto,
} from '@buspro/web/apis';
import {
  ErrorDisplayComponent,
  ExtendedProblemDetail,
  LocalAuthService,
  PasswordValidatorService,
  WEB_CONFIG,
  WebAppEnvironment,
  convertToProblemDetail,
  useFormErrorHandler,
} from '@buspro/web/core';
import { NgxAuthService } from '@corelabs/ng-auth';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardComponent } from 'ng-zorro-antd/card';
import { NzDescriptionsComponent, NzDescriptionsItemComponent } from 'ng-zorro-antd/descriptions';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { AvatarModule } from 'ngx-avatars';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'buspro-admin-profile-page',
  standalone: true,
  imports: [
    DatePipe,
    NgIf,
    NgClass,
    ReactiveFormsModule,
    NzButtonModule,
    NzCardComponent,
    NzDividerComponent,
    NzDescriptionsComponent,
    NzDescriptionsItemComponent,
    NzAlertModule,
    NzInputDirective,
    AvatarModule,
    ErrorDisplayComponent,
  ],
  template: `
    <div class="max-w-5xl mx-auto">
      <div class="mb-6">
        <h1 class="text-3xl font-bold text-gray-900">My Profile</h1>
        <p class="text-gray-600 mt-2">Manage your account settings and preferences</p>
      </div>

      @if (currentUser$()) {
        <!-- Profile Overview Card -->
        <nz-card nzTitle="Profile Information" class="mb-6">
          <div class="flex items-start gap-6">
            <ngx-avatars
              [name]="currentUser$()!.name"
              [size]="100"
            ></ngx-avatars>
            
            <div class="flex-1">
              <h2 class="text-2xl font-semibold text-gray-900">
                {{ currentUser$()!.firstName || '—' }} {{ currentUser$()!.surname || '' }}
              </h2>
              <p class="text-gray-600 mt-1">{{ currentUser$()!.email }}</p>
              <div class="mt-3">
                <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                  {{ currentUser$()!.role || 'Admin' }}
                </span>
              </div>
            </div>
          </div>

          <nz-divider></nz-divider>

          <nz-descriptions nzBordered [nzColumn]="2">
            <nz-descriptions-item nzTitle="First Name">
              {{ currentUser$()!.firstName }}
            </nz-descriptions-item>
            <nz-descriptions-item nzTitle="Surname">
              {{ currentUser$()!.surname }}
            </nz-descriptions-item>
            <nz-descriptions-item nzTitle="Email Address">
              {{ currentUser$()!.email }}
            </nz-descriptions-item>
            <nz-descriptions-item nzTitle="Phone Number">
              {{ currentUser$()!.phoneNumber || 'Not provided' }}
            </nz-descriptions-item>
            <nz-descriptions-item nzTitle="Role">
              {{ currentUser$()!.role || 'N/A' }}
            </nz-descriptions-item>
            <nz-descriptions-item nzTitle="Account Status">
              <span class="text-green-600 font-semibold">
                {{ currentUser$()!.activated ? 'Active' : 'Inactive' }}
              </span>
            </nz-descriptions-item>
            <nz-descriptions-item nzTitle="Email Verified" [nzSpan]="2">
              @if (currentUser$()!.isVerified) {
                <span class="text-green-600">✅ Verified</span>
              } @else {
                <span class="text-orange-600">⚠️ Not Verified</span>
              }
            </nz-descriptions-item>
            <nz-descriptions-item nzTitle="Account Created">
              {{ currentUser$()!.createdAt | date:'medium' }}
            </nz-descriptions-item>
            <nz-descriptions-item nzTitle="Last Updated">
              {{ currentUser$()!.updatedAt | date:'medium' }}
            </nz-descriptions-item>
          </nz-descriptions>
        </nz-card>

        <!-- Security Section -->
        <nz-card nzTitle="Security" class="mb-6">
          <div class="space-y-6">
            <div>
              <h3 class="text-xl font-semibold text-gray-900">Change Password</h3>
              <p class="text-sm text-gray-600">
                Update your BusPro admin password. We will sign you out from other sessions after a successful change for
                security.
              </p>
            </div>

            <div *ngIf="showError()" class="mb-2">
              <app-error-display
                [error]="errorDetails()"
                [showRetry]="true"
                (retry)="onRetry()"
                (dismiss)="onDismissError()"
              />
            </div>

            <nz-alert
              [nzType]="passwordRequirements.enforced ? 'info' : 'warning'"
              [nzMessage]="passwordRequirements.enforced ? 'Strong password required' : 'Relaxed password rules in effect'"
              [nzDescription]="passwordHint"
              nzShowIcon
            ></nz-alert>

            <form class="space-y-4" [formGroup]="passwordForm" (ngSubmit)="changePassword()" novalidate>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1" for="currentPassword">Current Password</label>
                <div class="relative">
                  <input
                    id="currentPassword"
                    type="{{ hideCurrentPassword() ? 'password' : 'text' }}"
                    nz-input
                    class="w-full"
                    formControlName="currentPassword"
                    placeholder="Enter your current password"
                    autocomplete="current-password"
                  />
                  <button
                    type="button"
                    class="btn btn-xs btn-ghost absolute right-2 top-1/2 -translate-y-1/2"
                    (click)="hideCurrentPassword.set(!hideCurrentPassword())"
                  >
                    {{ hideCurrentPassword() ? 'Show' : 'Hide' }}
                  </button>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1" for="newPassword">New Password</label>
                <div class="relative">
                  <input
                    id="newPassword"
                    type="{{ hideNewPassword() ? 'password' : 'text' }}"
                    nz-input
                    class="w-full"
                    formControlName="newPassword"
                    placeholder="Create a strong password"
                    autocomplete="new-password"
                  />
                  <button
                    type="button"
                    class="btn btn-xs btn-ghost absolute right-2 top-1/2 -translate-y-1/2"
                    (click)="hideNewPassword.set(!hideNewPassword())"
                  >
                    {{ hideNewPassword() ? 'Show' : 'Hide' }}
                  </button>
                </div>

                @if (passwordForm.get('newPassword')?.touched && passwordForm.get('newPassword')?.invalid) {
                  <p class="text-xs text-error mt-1">Please provide a password that meets the requirements above.</p>
                }

                <div class="mt-3">
                  <div class="flex items-center gap-2">
                    <div class="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        class="h-full transition-all duration-300"
                        [class.bg-red-500]="passwordStrength() < 40"
                        [class.bg-orange-500]="passwordStrength() >= 40 && passwordStrength() < 60"
                        [class.bg-yellow-500]="passwordStrength() >= 60 && passwordStrength() < 80"
                        [class.bg-green-500]="passwordStrength() >= 80"
                        [style.width.%]="passwordStrength()"
                      ></div>
                    </div>
                    <span class="text-xs font-medium" [ngClass]="passwordStrengthClass()">
                      {{ passwordStrengthLabel() }}
                    </span>
                  </div>
                  @if (passwordFeedback()) {
                    <p class="text-xs text-gray-500 mt-1">{{ passwordFeedback() }}</p>
                  }
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1" for="confirmPassword">Confirm Password</label>
                <div class="relative">
                  <input
                    id="confirmPassword"
                    type="{{ hideConfirmPassword() ? 'password' : 'text' }}"
                    nz-input
                    class="w-full"
                    formControlName="confirmPassword"
                    placeholder="Re-enter your new password"
                    autocomplete="new-password"
                  />
                  <button
                    type="button"
                    class="btn btn-xs btn-ghost absolute right-2 top-1/2 -translate-y-1/2"
                    (click)="hideConfirmPassword.set(!hideConfirmPassword())"
                  >
                    {{ hideConfirmPassword() ? 'Show' : 'Hide' }}
                  </button>
                </div>

                @if (passwordForm.get('confirmPassword')?.touched && passwordForm.get('confirmPassword')?.invalid) {
                  <p class="text-xs text-error mt-1">Please confirm your new password.</p>
                }
              </div>

              <button
                nz-button
                nzType="primary"
                class="w-full h-12 flex items-center justify-center gap-2"
                type="submit"
                [disabled]="passwordForm.invalid || changePasswordMutation.isPending()"
              >
                <span *ngIf="changePasswordMutation.isPending()" class="loading loading-spinner loading-sm"></span>
                {{ changePasswordMutation.isPending() ? 'Updating password...' : 'Change Password' }}
              </button>
            </form>

            @if (showSuccess()) {
              <nz-alert
                nzType="success"
                nzShowIcon
                nzMessage="Password updated"
                nzDescription="We signed you out of other sessions. Please sign in again with your new password."
              ></nz-alert>
            }

            <nz-divider></nz-divider>

            <div class="grid gap-6 lg:grid-cols-2">
              <div class="bg-white border rounded-lg shadow-sm p-4">
                <div class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900">Active Sessions</h3>
                    <p class="text-sm text-gray-500">Review signed-in devices and revoke sessions you don’t recognize.</p>
                  </div>
                  <button
                    class="btn btn-sm btn-ghost self-start md:self-auto"
                    type="button"
                    (click)="refreshSessions()"
                    [disabled]="sessionsQuery.isRefetching() || sessionsQuery.isPending()"
                  >
                    <span *ngIf="sessionsQuery.isRefetching() || sessionsQuery.isPending()" class="loading loading-spinner loading-xs"></span>
                    Refresh
                  </button>
                </div>

                @if (sessionsQuery.isPending()) {
                  <div class="py-6 flex justify-center">
                    <span class="loading loading-spinner loading-md text-primary"></span>
                  </div>
                } @else if (sessionsError()) {
                  <div class="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    <div class="font-semibold mb-1">We couldn’t load your sessions</div>
                    <div>{{ sessionsError()?.detail ?? 'Please try again in a moment.' }}</div>
                    <button class="btn btn-xs btn-outline mt-3" type="button" (click)="refreshSessions()">Retry</button>
                  </div>
                } @else if (!sessions().length) {
                  <div class="mt-4 p-4 bg-gray-50 border border-dashed border-gray-200 rounded-lg text-sm text-gray-500">
                    Only this device is signed in right now.
                  </div>
                } @else {
                  <div class="space-y-3 mt-4">
                    @for (session of sessions(); track session.id) {
                      <div class="border border-gray-200 rounded-lg p-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p class="text-base font-medium text-gray-900">{{ session.deviceName || 'Unknown Device' }}</p>
                          <p class="text-sm text-gray-500">
                            {{ session.ipAddress || 'Unknown IP' }} · {{ session.location || 'Unknown location' }}
                          </p>
                          <p class="text-xs text-gray-400">Last active: {{ session.lastActiveAt || session.createdAt | date:'medium' }}</p>
                        </div>
                        <div class="flex items-center gap-3">
                          @if (session.isCurrent) {
                            <span class="text-xs font-semibold uppercase text-green-600">Current session</span>
                          }
                          <button
                            class="btn btn-sm btn-outline"
                            type="button"
                            (click)="revokeSession(session.id)"
                            [disabled]="revokeSessionMutation.isPending()"
                          >
                            <span *ngIf="isSessionActionPending(session.id)" class="loading loading-spinner loading-xs"></span>
                            Sign out
                          </button>
                        </div>
                      </div>
                    }
                  </div>
                  @if (sessions().length > 1) {
                    <div class="flex justify-end mt-4">
                      <button
                        class="btn btn-sm btn-outline"
                        type="button"
                        (click)="revokeOtherSessions()"
                        [disabled]="revokeOtherSessionsMutation.isPending()"
                      >
                        <span *ngIf="revokeOtherSessionsMutation.isPending()" class="loading loading-spinner loading-xs"></span>
                        Sign out others
                      </button>
                    </div>
                  }
                }
              </div>

              <div class="bg-white border rounded-lg shadow-sm p-4">
                <div class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900">Recent Login Activity</h3>
                    <p class="text-sm text-gray-500">Latest sign-ins recorded for your admin account.</p>
                  </div>
                  <button
                    class="btn btn-sm btn-ghost"
                    type="button"
                    (click)="refreshLoginHistory()"
                    [disabled]="loginHistoryQuery.isRefetching() || loginHistoryQuery.isPending()"
                  >
                    <span *ngIf="loginHistoryQuery.isRefetching() || loginHistoryQuery.isPending()" class="loading loading-spinner loading-xs"></span>
                    Refresh
                  </button>
                </div>

                @if (loginHistoryQuery.isPending()) {
                  <div class="py-6 flex justify-center">
                    <span class="loading loading-spinner loading-md text-primary"></span>
                  </div>
                } @else if (loginHistoryError()) {
                  <div class="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    <div class="font-semibold mb-1">Unable to load login history</div>
                    <div>{{ loginHistoryError()?.detail ?? 'Please try again later.' }}</div>
                    <button class="btn btn-xs btn-outline mt-3" type="button" (click)="refreshLoginHistory()">Retry</button>
                  </div>
                } @else if (!loginHistory().length) {
                  <div class="mt-4 p-4 bg-gray-50 border border-dashed border-gray-200 rounded-lg text-sm text-gray-500">
                    No login activity recorded yet.
                  </div>
                } @else {
                  <div class="space-y-3 mt-4">
                    @for (entry of loginHistory(); track entry.id) {
                      <div class="border border-gray-200 rounded-lg p-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p class="text-base font-medium text-gray-900">{{ entry.createdAt | date:'medium' }}</p>
                          <p class="text-sm text-gray-500">
                            {{ entry.ipAddress || 'Unknown IP' }} · {{ entry.userAgent || 'Unknown device' }}
                          </p>
                          @if (getLoginLocation(entry.metadata)) {
                            <p class="text-xs text-gray-400">Location: {{ getLoginLocation(entry.metadata) }}</p>
                          }
                        </div>
                        <span
                          class="text-sm font-semibold"
                          [ngClass]="entry.successful ? 'text-green-600' : 'text-red-600'"
                        >
                          {{ entry.successful ? 'Successful' : 'Failed' }}
                        </span>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>

          </div>
        </nz-card>

      } @else {
        <div class="text-center py-12">
          <div class="loading loading-spinner loading-lg"></div>
          <p class="mt-4 text-gray-600">Loading profile...</p>
        </div>
      }
    </div>
  `,
})
export class AdminProfilePageComponent {
  private ngxAuthService = inject(NgxAuthService);
  private readonly localUsersApi = inject(LocalUsersApi);
  private notification = inject(NzNotificationService);
  private logger = inject(NGXLogger);
  private fb = inject(FormBuilder);
  private readonly environment = inject<WebAppEnvironment>(WEB_CONFIG);
  private readonly router = inject(Router);
  private readonly localAuthService = inject(LocalAuthService);
  private readonly formErrorHandler = useFormErrorHandler();

  currentUser$ = toSignal<LocalUserDto | null>(this.ngxAuthService.state, {
    initialValue: null,
  });

  showError = signal(false);
  errorDetails = signal<ExtendedProblemDetail | null>(null);
  showSuccess = signal(false);

  hideCurrentPassword = signal(true);
  hideNewPassword = signal(true);
  hideConfirmPassword = signal(true);

  passwordRequirements = this.getPasswordRequirements();
  passwordHint = PasswordValidatorService.getHintMessage(this.passwordRequirements);

  passwordStrength = signal(0);
  passwordFeedback = signal('');

  passwordForm = this.fb.group({
    currentPassword: ['', Validators.required],
    newPassword: ['', [Validators.required, PasswordValidatorService.createValidator(this.passwordRequirements)]],
    confirmPassword: ['', Validators.required],
  });

  pendingSessionId = signal<string | null>(null);

  currentUserId = computed(() => this.currentUser$()?.id ?? null);

  sessionsQuery = injectQuery(() => {
    const userId = this.currentUserId();
    return {
      queryKey: ['admin-local-sessions', userId],
      enabled: Boolean(userId),
      queryFn: () => {
        if (!userId) {
          return Promise.resolve([] as LocalUserSessionDto[]);
        }
        return lastValueFrom(this.localUsersApi.localUsersControllerGetUserSessions({ id: userId }));
      },
      staleTime: 1000 * 30,
      refetchOnWindowFocus: false,
    };
  });

  loginHistoryQuery = injectQuery(() => {
    const userId = this.currentUserId();
    return {
      queryKey: ['admin-local-login-history', userId],
      enabled: Boolean(userId),
      queryFn: () => {
        if (!userId) {
          return Promise.resolve([] as LocalUserLoginHistoryDto[]);
        }
        return lastValueFrom(this.localUsersApi.localUsersControllerGetUserLoginHistory({ id: userId, limit: 20 }));
      },
      staleTime: 1000 * 60,
      refetchOnWindowFocus: false,
    };
  });

  sessions = computed(() => this.sessionsQuery.data() ?? []);
  sessionsError = computed<ExtendedProblemDetail | null>(() => {
    const error = this.sessionsQuery.error();
    return error ? convertToProblemDetail(error) : null;
  });

  loginHistory = computed(() => this.loginHistoryQuery.data() ?? []);
  loginHistoryError = computed<ExtendedProblemDetail | null>(() => {
    const error = this.loginHistoryQuery.error();
    return error ? convertToProblemDetail(error) : null;
  });

  changePasswordMutation = injectMutation(() => ({
    mutationFn: async (payload: ChangeOwnPasswordDto) => {
      const userId = this.currentUser()?.id;
      if (!userId) {
        throw new Error('Unable to determine current user');
      }

      return lastValueFrom(
        this.localUsersApi.localUsersControllerChangePassword({
          id: userId,
          body: payload,
        }),
      );
    },
    onSuccess: (response: LocalPasswordChangeResponseDto) => {
      this.logger.info('Password changed successfully', response);
      this.clearErrors();
      this.showSuccess.set(true);
      this.passwordForm.reset();
      this.resetPasswordStrength();
      this.notification.success('✅ Password updated', response.message ?? 'Password changed successfully.');
      void this.handleLogoutAfterPasswordChange();
    },
    onError: (error: unknown) => {
      this.logger.error('Password change failed', error);
      const result = this.formErrorHandler.handleFormError(error, {
        formName: 'admin-change-password-form',
      });

      if (!result.shouldHandleGlobally) {
        this.errorDetails.set(result.problemDetail);
        this.showError.set(true);
      }
    },
  }));

  revokeSessionMutation = injectMutation(() => ({
    mutationFn: (sessionId: string) => {
      const userId = this.currentUserId();
      if (!userId) {
        throw new Error('Cannot revoke session without a user context');
      }
      return lastValueFrom(this.localUsersApi.localUsersControllerRevokeUserSession({ id: userId, sessionId }));
    },
    onSuccess: () => {
      this.notification.success('✅ Session revoked', 'The selected session has been signed out.');
      void this.sessionsQuery.refetch();
      void this.loginHistoryQuery.refetch();
      this.pendingSessionId.set(null);
    },
    onError: (error: unknown) => {
      this.pendingSessionId.set(null);
      this.handleSecurityError(error, 'Unable to revoke session');
    },
  }));

  revokeOtherSessionsMutation = injectMutation(() => ({
    mutationFn: () => {
      const userId = this.currentUserId();
      if (!userId) {
        throw new Error('Cannot revoke other sessions without a user context');
      }
      return lastValueFrom(this.localUsersApi.localUsersControllerRevokeUserSessions({ id: userId }));
    },
    onSuccess: (response: RevokeAllSessionsResponseDto) => {
      this.notification.success(
        '✅ Signed out other sessions',
        response?.message ?? 'All other sessions were revoked successfully.',
      );
      void this.sessionsQuery.refetch();
      void this.loginHistoryQuery.refetch();
      this.pendingSessionId.set(null);
    },
    onError: (error: unknown) => {
      this.pendingSessionId.set(null);
      this.handleSecurityError(error, 'Unable to revoke other sessions');
    },
  }));

  constructor() {
    this.passwordForm.get('newPassword')?.valueChanges.subscribe((value) => this.updatePasswordStrength(value ?? ''));
  }

  currentUser = computed(() => this.currentUser$());

  changePassword(): void {
    this.clearErrors();
    this.showSuccess.set(false);

    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    const { newPassword, confirmPassword, currentPassword } = this.passwordForm.value;

    if (newPassword !== confirmPassword) {
      this.passwordForm.get('confirmPassword')?.setErrors({ mismatch: true });
      this.showError.set(true);
      this.errorDetails.set({
        title: 'Passwords do not match',
        detail: 'Please confirm that both password fields are identical.',
      } as ExtendedProblemDetail);
      return;
    }

    this.changePasswordMutation.mutate({
      currentPassword: currentPassword ?? '',
      newPassword: newPassword ?? '',
      confirmPassword: confirmPassword ?? '',
    });
  }

  onRetry(): void {
    this.clearErrors();
    this.changePassword();
  }

  onDismissError(): void {
    this.clearErrors();
  }

  passwordStrengthLabel(): string {
    const strength = this.passwordStrength();
    if (strength >= 80) return 'Strong';
    if (strength >= 60) return 'Good';
    if (strength >= 40) return 'Weak';
    return 'Very Weak';
  }

  passwordStrengthClass(): string {
    const strength = this.passwordStrength();
    if (strength >= 80) return 'text-green-600';
    if (strength >= 60) return 'text-amber-600';
    return 'text-red-600';
  }

  private getPasswordRequirements() {
    const strictValidationEnabled = this.environment.features?.strictPasswordValidation ?? true;

    if (!strictValidationEnabled) {
      return {
        minLength: 3,
        requireUppercase: false,
        requireLowercase: false,
        requireNumber: false,
        requireSpecialChar: false,
        enforced: false,
      };
    }

    return {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumber: true,
      requireSpecialChar: true,
      enforced: true,
    };
  }

  private updatePasswordStrength(password: string): void {
    let strength = 0;

    strength += Math.min(password.length * 4, 40);
    if (/[A-Z]/.test(password)) strength += 15;
    if (/[a-z]/.test(password)) strength += 15;
    if (/\d/.test(password)) strength += 15;
    if (/[@$!%*?&]/.test(password)) strength += 15;

    this.passwordStrength.set(Math.min(strength, 100));

    if (strength >= 80) {
      this.passwordFeedback.set('Great job! Your password is very strong.');
    } else if (strength >= 60) {
      this.passwordFeedback.set('Good password. Add more variety to make it even stronger.');
    } else if (strength >= 40) {
      this.passwordFeedback.set('This password is weak. Consider adding uppercase letters, numbers, or symbols.');
    } else {
      this.passwordFeedback.set('Very weak password. Increase the length and mix of characters.');
    }
  }

  private resetPasswordStrength(): void {
    this.passwordStrength.set(0);
    this.passwordFeedback.set('');
  }

  private clearErrors(): void {
    this.showError.set(false);
    this.errorDetails.set(null);
  }

  refreshSessions(): void {
    void this.sessionsQuery.refetch();
  }

  refreshLoginHistory(): void {
    void this.loginHistoryQuery.refetch();
  }

  revokeSession(sessionId: string): void {
    this.pendingSessionId.set(sessionId);
    this.revokeSessionMutation.mutate(sessionId, {
      onSettled: () => this.pendingSessionId.set(null),
    });
  }

  revokeOtherSessions(): void {
    this.pendingSessionId.set('bulk');
    this.revokeOtherSessionsMutation.mutate(undefined, {
      onSettled: () => this.pendingSessionId.set(null),
    });
  }

  isSessionActionPending(sessionId: string): boolean {
    return this.revokeSessionMutation.isPending() && this.pendingSessionId() === sessionId;
  }

  getLoginLocation(metadata: Record<string, unknown> | null | undefined): string | null {
    if (!metadata) {
      return null;
    }
    const location = metadata['location'];
    return typeof location === 'string' && location.length > 0 ? location : null;
  }

  private async handleLogoutAfterPasswordChange(): Promise<void> {
    try {
      this.ngxAuthService.logout();
    } catch (error) {
      this.logger.warn('Failed to revoke session during logout', error);
    } finally {
      this.localAuthService.logout();
    }
  }

  private handleSecurityError(error: unknown, message: string): void {
    this.logger.error(message, error);
    const result = this.formErrorHandler.handleFormError(error, {
      formName: 'admin-session-security',
    });

    if (!result.shouldHandleGlobally) {
      const description = result.problemDetail?.detail ?? message;
      this.notification.error('⚠️ Security update failed', description);
    }
  }
}
